import {
  type DocumentData,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
  type QueryConstraint,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { firestore } from '@core/services/firebase/firebase-service'
import { fromFirestoreInstagramReel } from '@core/services/instagram-reels/converters/instagram-reel-converter'
import { InstagramReelsErrorConverter } from '@core/services/instagram-reels/converters/instagram-reels-error-converter'
import { CoreError, CoreErrorCause } from '@domain/models/core-error'
import type { InstagramMediaKind, InstagramReel } from '@domain/models/instagram-reel'

const INSTAGRAM_REELS_COLLECTION = 'instagram_reels'
const DEFAULT_HOME_REELS_LIMIT = 3
const DEFAULT_PAGE_SIZE = 12

export interface PaginatedInstagramReelsResult {
  reels: InstagramReel[]
  lastDoc: QueryDocumentSnapshot<DocumentData> | null
  hasMore: boolean
}

type InstagramMediaKindFilter = Extract<InstagramMediaKind, 'reel' | 'post'>

export interface InstagramReelsQueryFilters {
  mediaKind?: InstagramMediaKindFilter
}

function getInstagramReelsCollection() {
  if (!firestore) {
    throw new CoreError({
      cause: CoreErrorCause.Unknown,
      context: 'Firestore is not initialized.',
    })
  }
  return collection(firestore, INSTAGRAM_REELS_COLLECTION)
}

function getInstagramReelDoc(reelId: string) {
  if (!firestore) {
    throw new CoreError({
      cause: CoreErrorCause.Unknown,
      context: 'Firestore is not initialized.',
    })
  }
  return doc(firestore, INSTAGRAM_REELS_COLLECTION, reelId)
}

function buildActiveInstagramReelsQuery(
  accountId: string,
  pageSize: number,
  lastDocument?: QueryDocumentSnapshot<DocumentData> | null,
  filters?: InstagramReelsQueryFilters,
) {
  const constraints: QueryConstraint[] = [
    where('accountId', '==', accountId),
    where('isActive', '==', true),
  ]

  if (filters?.mediaKind) {
    constraints.push(where('mediaKind', '==', filters.mediaKind))
  }

  constraints.push(orderBy('timestamp', 'desc'))

  if (lastDocument) {
    constraints.push(startAfter(lastDocument))
  }

  constraints.push(limit(pageSize))
  return query(getInstagramReelsCollection(), ...constraints)
}

function toSortableTimestampMs(value: number | null): number {
  return typeof value === 'number' && Number.isFinite(value)
    ? value
    : Number.NEGATIVE_INFINITY
}

function sortInstagramContentChronologically(reels: InstagramReel[]): InstagramReel[] {
  return reels
    .map((reel, index) => ({ reel, index }))
    .sort((left, right) => {
      const leftTimestamp = toSortableTimestampMs(left.reel.timestampMs)
      const rightTimestamp = toSortableTimestampMs(right.reel.timestampMs)

      if (leftTimestamp === rightTimestamp) {
        return left.index - right.index
      }

      return rightTimestamp - leftTimestamp
    })
    .map((entry) => entry.reel)
}

function mapNormalizedInstagramReels(
  docs: QueryDocumentSnapshot<DocumentData>[],
): InstagramReel[] {
  const normalizedReels = docs.flatMap((docSnap) => {
    try {
      return [fromFirestoreInstagramReel(docSnap)]
    } catch {
      return []
    }
  })

  return sortInstagramContentChronologically(normalizedReels)
}

function isReelAccessible(reel: InstagramReel, accountId: string): boolean {
  return reel.accountId === accountId && reel.isActive
}

export class InstagramReelsService {
  private static instance: InstagramReelsService

  private constructor() {}

  public static getInstance(): InstagramReelsService {
    if (!InstagramReelsService.instance) {
      InstagramReelsService.instance = new InstagramReelsService()
    }
    return InstagramReelsService.instance
  }

  public async fetchLatestInstagramReels(
    accountId: string,
    count = DEFAULT_HOME_REELS_LIMIT,
    filters?: InstagramReelsQueryFilters,
  ): Promise<InstagramReel[]> {
    try {
      const safeLimit = count > 0 ? count : DEFAULT_HOME_REELS_LIMIT
      const snapshot = await getDocs(
        buildActiveInstagramReelsQuery(accountId, safeLimit, undefined, filters),
      )
      return mapNormalizedInstagramReels(snapshot.docs)
    } catch (error) {
      throw InstagramReelsErrorConverter.domainModel(error)
    }
  }

  public async fetchInstagramReelsPaginated(
    accountId: string,
    pageSize: number,
    lastDocument?: QueryDocumentSnapshot<DocumentData> | null,
    filters?: InstagramReelsQueryFilters,
  ): Promise<PaginatedInstagramReelsResult> {
    try {
      const safePageSize = pageSize > 0 ? pageSize : DEFAULT_PAGE_SIZE
      const snapshot = await getDocs(
        buildActiveInstagramReelsQuery(
          accountId,
          safePageSize,
          lastDocument,
          filters,
        ),
      )
      const reels = mapNormalizedInstagramReels(snapshot.docs)
      const lastDoc =
        snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null

      return {
        reels,
        lastDoc,
        hasMore: snapshot.docs.length >= safePageSize,
      }
    } catch (error) {
      throw InstagramReelsErrorConverter.domainModel(error)
    }
  }

  public async fetchInstagramReel(
    reelId: string,
    accountId: string,
  ): Promise<InstagramReel> {
    try {
      const directDoc = await getDoc(getInstagramReelDoc(reelId))
      if (directDoc.exists()) {
        const reel = fromFirestoreInstagramReel(directDoc)
        if (isReelAccessible(reel, accountId)) {
          return reel
        }
      }

      const fallbackQuery = query(
        getInstagramReelsCollection(),
        where('reelId', '==', reelId),
        where('accountId', '==', accountId),
        where('isActive', '==', true),
        limit(1),
      )
      const fallbackSnapshot = await getDocs(fallbackQuery)
      const fallbackDoc = fallbackSnapshot.docs[0]
      if (fallbackDoc) {
        return fromFirestoreInstagramReel(fallbackDoc)
      }

      throw new CoreError({
        cause: CoreErrorCause.DataNotFound,
        context: `Instagram content "${reelId}" was not found.`,
      })
    } catch (error) {
      throw InstagramReelsErrorConverter.domainModel(error)
    }
  }
}

const instagramReelsService = InstagramReelsService.getInstance()

export async function fetchLatestInstagramReels(
  accountId: string,
  count = DEFAULT_HOME_REELS_LIMIT,
  filters?: InstagramReelsQueryFilters,
): Promise<InstagramReel[]> {
  return instagramReelsService.fetchLatestInstagramReels(accountId, count, filters)
}

export async function fetchInstagramReelsPaginated(
  accountId: string,
  pageSize: number,
  lastDocument?: QueryDocumentSnapshot<DocumentData> | null,
  filters?: InstagramReelsQueryFilters,
): Promise<PaginatedInstagramReelsResult> {
  return instagramReelsService.fetchInstagramReelsPaginated(
    accountId,
    pageSize,
    lastDocument,
    filters,
  )
}

export async function fetchInstagramReel(
  reelId: string,
  accountId: string,
): Promise<InstagramReel> {
  return instagramReelsService.fetchInstagramReel(reelId, accountId)
}
